import clsx from 'clsx'
import { PropertyValueMap, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { Size } from '../../common/types'
import { DdsElement } from '../../internal/dds-hu-element'
import styles from './icon-button.scss?inline'

export type ButtonVariant = 'neutral' | 'brand' | 'inverted' | 'inverted-brand'
export type HtmlButtonType = 'button' | 'submit' | 'reset'
export type ButtonSize = Extract<Size, 'lg' | 'md' | 'sm' | 'xs'>

/**
 * `dap-ds-icon-button`
 * @summary An icon button is a button with an icon.
 * @element dap-ds-icon-button
 * @title - Icon button
 *
 * @property {string} ariaLabel - The aria label of the button.
 *
 * @slot - The icon of the button.
 *
 * @csspart base - The main button container.
 * @csspart content - The icon of the button.
 *
 * @cssproperty --dds-icon-button-border-width - The width of the button's border. Defaults to var(--dds-border-width-base).
 * @cssproperty --dds-icon-button-border-color - The color of the button's border. Defaults to var(--dds-border-neutral-transparent-interactive).
 * @cssproperty --dds-icon-button-border-radius - The border radius of the button. Defaults to var(--dds-radius-rounded).
 * @cssproperty --dds-icon-button-hover-border-width - The width of the button's border on hover. Defaults to var(--dds-border-width-large).
 * @cssproperty --dds-icon-button-active-border-width - The width of the button's border when active. Defaults to var(--dds-border-width-xlarge).
 * @cssproperty --dds-icon-button-transition - The transition property for the button. Defaults to var(--dds-transition-all).
 * @cssproperty --dds-icon-button-cursor - The cursor style for the button. Defaults to pointer.
 * @cssproperty --dds-icon-button-disabled-cursor - The cursor style for the disabled button. Defaults to not-allowed.
 *
 * @cssproperty --dds-icon-button-column-gap - The gap between icon and text. Defaults to var(--dds-spacing-100).
 * @cssproperty --dds-icon-button-padding-lg - Padding for large size. Defaults to var(--dds-spacing-0).
 * @cssproperty --dds-icon-button-padding-md - Padding for medium size. Defaults to var(--dds-spacing-100).
 * @cssproperty --dds-icon-button-padding-sm - Padding for small size. Defaults to var(--dds-spacing-50).
 * @cssproperty --dds-icon-button-padding-xs - Padding for extra small size. Defaults to var(--dds-spacing-25).
 *
 * @cssproperty --dds-icon-button-size-lg - Size for large button. Defaults to var(--dds-spacing-800).
 * @cssproperty --dds-icon-button-size-md - Size for medium button. Defaults to var(--dds-spacing-600).
 * @cssproperty --dds-icon-button-size-sm - Size for small button. Defaults to var(--dds-spacing-400).
 * @cssproperty --dds-icon-button-size-xs - Size for extra small button. Defaults to var(--dds-spacing-300).
 *
 * @cssproperty --dds-icon-button-neutral-bg - Background color for neutral variant. Defaults to var(--dds-transparent-black-subtle).
 * @cssproperty --dds-icon-button-neutral-color - Text color for neutral variant. Defaults to var(--dds-icon-neutral-base).
 * @cssproperty --dds-icon-button-neutral-hover-bg - Hover background color for neutral variant. Defaults to var(--dds-transparent-black-base).
 * @cssproperty --dds-icon-button-neutral-active-bg - Active background color for neutral variant. Defaults to var(--dds-transparent-black-medium).
 *
 * @cssproperty --dds-icon-button-brand-bg - Background color for brand variant. Defaults to var(--dds-button-subtle-background-enabled).
 * @cssproperty --dds-icon-button-brand-color - Text color for brand variant. Defaults to var(--dds-button-subtle-icon-enabled).
 * @cssproperty --dds-icon-button-brand-hover-bg - Hover background color for brand variant. Defaults to var(--dds-button-subtle-background-hover).
 * @cssproperty --dds-icon-button-brand-active-bg - Active background color for brand variant. Defaults to var(--dds-button-subtle-background-pressed).
 *
 * @cssproperty --dds-icon-button-inverted-bg - Background color for inverted variant. Defaults to var(--dds-transparent-white-subtle).
 * @cssproperty --dds-icon-button-inverted-color - Text color for inverted variant. Defaults to var(--dds-button-primary-icon-enabled).
 * @cssproperty --dds-icon-button-inverted-hover-bg - Hover background color for inverted variant. Defaults to var(--dds-transparent-white-base).
 * @cssproperty --dds-icon-button-inverted-active-bg - Active background color for inverted variant. Defaults to var(--dds-transparent-white-medium).
 *
 * @cssproperty --dds-icon-button-inverted-brand-bg - Background color for inverted brand variant. Defaults to var(--dds-button-primary-background-enabled).
 * @cssproperty --dds-icon-button-inverted-brand-color - Text color for inverted brand variant. Defaults to var(--dds-button-primary-icon-enabled).
 * @cssproperty --dds-icon-button-inverted-brand-hover-bg - Hover background color for inverted brand variant. Defaults to var(--dds-button-primary-background-enabled).
 * @cssproperty --dds-icon-button-inverted-brand-active-bg - Active background color for inverted brand variant. Defaults to var(--dds-button-primary-background-enabled).
 *
 * @cssproperty --dds-icon-button-disabled-color - Color for disabled state. Defaults to var(--dds-icon-neutral-disabled).
 */
@customElement('dap-ds-icon-button')
export default class DapDSIconButton extends DdsElement {
  /** The type of the button.
   * @type {'neutral' | 'brand' | 'inverted' | 'inverted-brand'}
   */
  @property({ reflect: true }) variant?: ButtonVariant = 'neutral'

  /** Adds a transparent background to the button */
  @property({ type: Boolean, reflect: true }) clean? = false

  /** The size of the icon
   * @type {'lg' | 'md' | 'sm' | 'xs'}
   */
  @property({ reflect: true }) size: ButtonSize = 'md'

  /**
   * The size of the icon in pixels. By default the icon gets the size of the button. This property overwrites the size of the icon.
   */
  @property({ type: Number }) iconSize: number

  /** The disabled state of the button. */
  @property({ type: Boolean, reflect: true }) disabled = false

  /**
   * The type of the button.
   * @type {'button' | 'submit' | 'reset'}
   */
  @property() htmlType: HtmlButtonType = 'button'

  /** The icon of the button. You can use the icon names from the icon component */
  @property() icon: string

  /** The name of the button */
  @property() name: string

  static readonly styles = unsafeCSS(styles)

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)

    if (this.shadowRoot) {
      const iconSlot = this.shadowRoot.querySelector('slot') as HTMLSlotElement
      if (iconSlot) {
        if (
          iconSlot.assignedElements().length === 1 &&
          this.iconSize === undefined
        ) {
          iconSlot.assignedElements()[0].setAttribute('size', this.size)
        }

        if (
          iconSlot.assignedElements().length === 1 &&
          this.iconSize !== undefined
        ) {
          iconSlot
            .assignedElements()[0]
            .setAttribute('staticSize', this.iconSize.toString())
          iconSlot
            .assignedElements()[0]
            .setAttribute('size', this.iconSize.toString())
        }
      }
    }
  }

  focus() {
    this.shadowRoot?.querySelector('button')?.focus()
  }

  render() {
    return html`
      <button
        part="base"
        .disabled=${this.disabled}
        aria-label=${ifDefined(this.ariaLabel) || ''}
        type=${this.htmlType}
        class=${clsx(
          'icon-button',
          {
            [`icon-button--${this.size}`]:
              this.size && this.iconSize === undefined,
          },
          { 'icon-button--disabled': this.disabled },
          { 'icon-button--clean': this.clean },
          `icon-button--${this.variant}`,
        )}>
        <div part="high-contrast" class="button__highcontrast"></div>
        ${this.icon
          ? html` <dap-ds-icon
              part="content"
              exportparts="icon: icon-base"
              name="${this.icon}"
              aria-hidden="true"
              aria-label=${ifDefined(this.ariaLabel) || ''}
              staticSize=${ifDefined(this.iconSize)}
              size=${this.size}>
            </dap-ds-icon>`
          : html` <slot></slot> `}
      </button>
    `
  }
}
